"use client";
import React, { forwardRef, ComponentProps, ReactNode } from "react";
import {
  FadeInFromBottom,
  FadeInWithPulse,
} from "../../Animation/Animation.tsx";
import { MouseEventHandler } from "react";
import { CustomLink } from "../CustomLink.tsx";
import styles from "./Button.module.scss";
import { useRouter } from "next/compat/router";
import { removeExtraSpacesFromClassNames } from "../../../utils/removeExtraSpacesFromClassNames.ts";
import { useGlobalContext } from "@aleksundshantu.com/aunds-utils-react/contexts";
import { ReactSVG } from "react-svg";

export type ButtonTypes = "primary" | "secondary";

interface ButtonProps extends ComponentProps<"a"> {
  type: ButtonTypes;
  children: ReactNode;
  animation?: "fade" | "pulse" | "none";
  /** Renders normal button component with on Click Event Handler*/
  onClick?: MouseEventHandler<HTMLElement>;
  href: string;
  className?: string;
  target: string;
  /**
   * Creates a link with no prefetching abilities... which Next Links per default have
   */
  disablePrefetch?: boolean;
  /**
   * Creates a link which triggers prefetching only on hover... Next Links prefetch per default as soon as link is visible
   */
  onlyPrefetchOnHover?: boolean;
  isDownload?: boolean;
  isBack?: boolean;
  icon?: string;
  noIcon?: boolean;
  dangerously?: boolean;
}

export const Button = forwardRef<HTMLElement, ButtonProps>(
  (
    {
      type,
      children,
      animation,
      onClick,
      href = "_self",
      className,
      target = "_self",
      disablePrefetch = false,
      onlyPrefetchOnHover = false,
      isDownload = false,
      isBack = false,
      icon,
      noIcon = false,
      dangerously = false,
      ...props
    }: ButtonProps,
    ref,
  ) => {
    const { asPath } = useRouter() ?? {};
    const { getIcon } = useGlobalContext();
    const btnClassName = removeExtraSpacesFromClassNames(
      `btn ${styles.btn} ${styles[`is-${isDownload ? "secondary" : type}`]} is-${isDownload ? "secondary" : type} ${className || ""} ${isBack ? styles["is-back"] : ""} ${isDownload ? "is-download" : ""}`,
    );

    if (!href && !onClick) {
      throw new Error(`No href provided at "Button.tsx" at ${asPath}`);
    }
    // else if (!target && !onClick) {
    //   throw new Error(`No target provided at "Button.tsx" at ${asPath}`);
    // }

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    ) => {
      onClick?.(e);
    };
    const contentSpan =
      dangerously && typeof children === "string" ? (
        <span dangerouslySetInnerHTML={{ __html: children }} />
      ) : (
        <span>{children}</span>
      );

    const innerButtonJSX =
      !!href && !onClick ? (
        <CustomLink
          href={href}
          target={target}
          className={btnClassName}
          disablePrefetch={disablePrefetch}
          onlyPrefetchOnHover={onlyPrefetchOnHover}
          ref={ref as React.Ref<HTMLAnchorElement>}
          {...props}
        >
          <span>{children}</span>
          {/* jscpd:ignore-start */}
          {type === "secondary" && !isDownload && (
            <ReactSVG
              src={getIcon("circleArrowRight")}
              wrapper={"svg"}
              width={24}
              height={24}
              aria-hidden
              beforeInjection={(svg) => {
                svg.setAttribute("alt", `Circle Arrow right Icon`);
                svg.setAttribute("aria-label", `Circle Arrow right Icon`);
              }}
            />
          )}
          {/* jscpd:ignore-end */}
          {isDownload && (
            <ReactSVG
              src={getIcon("download")}
              wrapper={"svg"}
              width={16}
              height={16}
              aria-hidden
              beforeInjection={(svg) => {
                svg.setAttribute("alt", `Download Icon`);
                svg.setAttribute("aria-label", `Download Icon`);
              }}
              className="download-icon"
            />
          )}
          {/* jscpd:ignore-start */}
          {type === "primary" && !isDownload && (
            <ReactSVG
              src={icon ? getIcon(icon) : getIcon("circleArrowRight")}
              wrapper={"svg"}
              width={24}
              height={24}
              aria-hidden
              beforeInjection={(svg) => {
                svg.setAttribute("alt", `Circle Arrow right Icon`);
                svg.setAttribute("aria-label", `Circle Arrow right Icon`);
              }}
            />
          )}
          {/* jscpd:ignore-end */}
        </CustomLink>
      ) : (
        onClick && (
          <button
            onClick={handleClick}
            className={`${btnClassName} button-reset`}
            aria-label={props["aria-label"] ?? ""}
            ref={ref as React.Ref<HTMLButtonElement>}
            {...(props as ComponentProps<"button">)}
            data-custom-icon={icon ? "true" : "false"}
          >
            {contentSpan}
            {/* jscpd:ignore-start */}
            {!noIcon && type === "secondary" && !isDownload && (
              <ReactSVG
                src={getIcon("circleArrowRight")}
                wrapper={"svg"}
                width={24}
                height={24}
                aria-hidden
                beforeInjection={(svg) => {
                  svg.setAttribute("alt", `Circle Arrow right Icon`);
                  svg.setAttribute("aria-label", `Circle Arrow right Icon`);
                }}
              />
            )}
            {/* jscpd:ignore-end */}
            {!noIcon && isDownload && (
              <ReactSVG
                src={getIcon("download")}
                wrapper={"svg"}
                width={24}
                height={24}
                aria-hidden
                beforeInjection={(svg) => {
                  svg.setAttribute("alt", `Download Icon`);
                  svg.setAttribute("aria-label", `Download Icon`);
                }}
                className="download-icon"
              />
            )}
            {/* jscpd:ignore-start */}
            {!noIcon && type === "primary" && !isDownload && (
              <ReactSVG
                src={icon ? getIcon(icon) : getIcon("circleArrowRight")}
                wrapper={"svg"}
                width={24}
                height={24}
                aria-hidden
                beforeInjection={(svg) => {
                  svg.setAttribute("alt", `Circle Arrow right Icon`);
                  svg.setAttribute("aria-label", `Circle Arrow right Icon`);
                }}
              />
            )}
            {/* jscpd:ignore-end */}
          </button>
        )
      );

    if (!innerButtonJSX) {
      return null;
    }

    return animation === "fade" ? (
      <FadeInFromBottom>{innerButtonJSX}</FadeInFromBottom>
    ) : animation === "pulse" ? (
      <FadeInWithPulse>{innerButtonJSX}</FadeInWithPulse>
    ) : (
      innerButtonJSX
    );
  },
);
Button.displayName = "Button";

export const SubmitButton = forwardRef<
  HTMLButtonElement,
  { children: React.ReactNode; className?: string; icon?: string }
>(({ children, className, icon }, ref) => {
  const { getIcon } = useGlobalContext();
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="submit"
      className={`btn ${styles.btn} ${styles["is-primary"]} button-reset mx-auto ${className || ""}`}
    >
      <span>{children}</span>
      {icon && (
        <ReactSVG
          src={getIcon(icon)}
          wrapper={"svg"}
          width={24}
          height={24}
          aria-hidden
          beforeInjection={(svg) => {
            svg.setAttribute("aria-hidden", `true`);
          }}
        />
      )}
    </button>
  );
});

SubmitButton.displayName = "SubmitButton";
